import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, timer } from 'rxjs';
import { IApiReservation, IDispoResponse, IUserReservation } from '../../interfaces/reservation.interface';
import { CacheService } from '../caches/cache.service';
import { ReservationApiService } from './reservation-api.service';
import { ReservationNotificationService } from './reservation-notification.service';

@Injectable({
  providedIn: 'root'
})
export class ReservationStateService {
  #apiService: ReservationApiService = inject(ReservationApiService);
  #notificationService: ReservationNotificationService = inject(ReservationNotificationService);
  #cacheService: CacheService = inject(CacheService);
  #destroyRef: DestroyRef = inject(DestroyRef);

  #loading: WritableSignal<boolean> = signal<boolean>(false);
  #loadingDisponibilities: WritableSignal<boolean> = signal<boolean>(false);
  #loadingReservations: WritableSignal<boolean> = signal<boolean>(false);

  #userReservation: WritableSignal<IUserReservation[]> = signal<IUserReservation[]>([]);
  #hairdresserReservation: WritableSignal<IApiReservation[]> = signal<IApiReservation[]>([]);
  #disponibilities: WritableSignal<IDispoResponse[]> = signal<IDispoResponse[]>([]);
  #isPolling: WritableSignal<boolean> = signal(false);
  #lastUpdateTime: WritableSignal<Date> = signal(new Date());
  #updatingReservations: WritableSignal<Set<number>> = signal(new Set());

  loading: Signal<boolean> = this.#loading.asReadonly();
  loadingDisponibilities: Signal<boolean> = this.#loadingDisponibilities.asReadonly();
  loadingReservations: Signal<boolean> = this.#loadingReservations.asReadonly();
  userReservation: Signal<IUserReservation[]> = this.#userReservation.asReadonly();
  hairdresserReservation: Signal<IApiReservation[]> = this.#hairdresserReservation.asReadonly();
  disponibilities: Signal<IDispoResponse[]> = this.#disponibilities.asReadonly();
  updatingReservations = this.#updatingReservations.asReadonly();
  isPolling = this.#isPolling.asReadonly();
  lastUpdateTime = this.#lastUpdateTime.asReadonly();

  private readonly POLLING_INTERVAL = 30000; // 30 secondes

  getUserReservations(): void {
    this.#loading.set(true);
    this.#apiService.getUserReservations().subscribe({
      next: (res: IUserReservation[]) => {
        this.#userReservation.set(res);
      },
      error: (err) => {
        console.error('Error fetching user reservations:', err);
      },
      complete: () => {
        this.#loading.set(false);
      }
    });
  }

  getHairdresserReservations(forceRefresh: boolean = false): void {
    const cacheKey = 'reservations_hairdresser';

    if (forceRefresh) {
      this.#cacheService.invalidate(cacheKey);
    }

    this.#loading.set(true);
    const previousReservations = this.#hairdresserReservation();

    this.#cacheService.getOrFetch(
      cacheKey,
      () => this.#apiService.getHairdresserReservations(),
      {
        ttl: 2 * 60 * 1000, // 2 minutes pour les réservations (très volatiles)
        persistToStorage: false // Ne pas persister les réservations sensibles
      }
    ).subscribe({
      next: (res: IApiReservation[]) => {

        if (previousReservations.length > 0) {
          const newReservations = this.detectNewReservations(previousReservations, res);

          if (newReservations.length > 0) {
            this.#notificationService.notifyNewReservations(newReservations);
          }
        }

        this.#lastUpdateTime.set(new Date());
        this.#hairdresserReservation.set(res);
      },
      error: (err) => {
        console.error('Error fetching reservations:', err);
      },
      complete: () => {
        this.#loading.set(false);
      }
    });
  }

  getDisponibilities(hairdresserId: number): void {
    this.#loadingDisponibilities.set(true);
    this.#apiService.getDisponibilities(hairdresserId).subscribe({
      next: (res: IDispoResponse[]) => {
        this.#disponibilities.set(res);
      },
      error: (err) => {
        console.error('Error loading disponibilities:', err);
        this.#disponibilities.set([]);
      },
      complete: () => {
        this.#loadingDisponibilities.set(false);
      }
    });
  }

  startAutoUpdate(): void {
    if (this.#isPolling()) return; // Déjà en cours

    this.#isPolling.set(true);

    // Polling avec gestion intelligente de l'intervalle
    timer(0, this.POLLING_INTERVAL).pipe(
      switchMap(() => {
        return this.#apiService.getHairdresserReservations();
      }),
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe({
      next: (res: IApiReservation[]) => {
        const previousReservations = this.#hairdresserReservation();

        if (previousReservations.length > 0) {
          const newReservations = this.detectNewReservations(previousReservations, res);
          if (newReservations.length > 0) {
            this.#notificationService.notifyNewReservations(newReservations);
          }
        }

        this.#lastUpdateTime.set(new Date());
        this.#hairdresserReservation.set(res);
      },
      error: (error) => {
        console.error('Erreur dans le polling des réservations:', error);
        this.#isPolling.set(false);
      }
    });
  }

  stopAutoUpdate(): void {
    this.#isPolling.set(false);
  }

  forceUpdate(): void {
    this.getHairdresserReservations(true);
  }

  detectNewReservations(previous: IApiReservation[], current: IApiReservation[]): IApiReservation[] {
    const previousIds = new Set(previous.map(r => r.id));
    return current.filter(reservation => !previousIds.has(reservation.id));
  }

  updateReservationStatusLocally(reservationId: number, newStatus: "attente" | "attente-confirmation" | "accepte" | "updated" | "refuse" | "annule" | "termine"): void {
    const currentReservations = this.#hairdresserReservation();
    const updatedReservations = currentReservations.map(reservation =>
      reservation.id === reservationId
        ? { ...reservation, state: newStatus }
        : reservation
    );

    this.#hairdresserReservation.set(updatedReservations);
  }

  invalidateReservationCache(): void {
    this.#cacheService.invalidateByPrefix('reservations_');
    this.#cacheService.invalidateByPrefix('dashboard_stats');
  }

  rollbackReservationStatus(reservationId: number): void {

    // Tenter de récupérer depuis le cache
    const cachedData = this.#cacheService.get<IApiReservation[]>('reservations_hairdresser');
    if (cachedData) {
      this.#hairdresserReservation.set(cachedData);
    } else {
      // Si pas de cache, recharger depuis l'API
      this.forceUpdate();
    }
  }

  addToUpdatingList(reservationId: number): void {
    const currentUpdating = this.#updatingReservations();
    const newUpdating = new Set(currentUpdating);
    newUpdating.add(reservationId);
    this.#updatingReservations.set(newUpdating);
  }

  removeFromUpdatingList(reservationId: number): void {
    const currentUpdating = this.#updatingReservations();
    const newUpdating = new Set(currentUpdating);
    newUpdating.delete(reservationId);
    this.#updatingReservations.set(newUpdating);
  }

  isReservationUpdating(reservationId: number): boolean {
    return this.#updatingReservations().has(reservationId);
  }

  updateDisponibilitiesLocally(dispos: IDispoResponse[]): void {
    this.#disponibilities.set(dispos);
  }

  removeDisponibilityLocally(id: number): void {
    const currentDispos = this.#disponibilities();
    const updatedDispos = currentDispos.filter(dispo => dispo.dispo.id !== id);
    this.#disponibilities.set(updatedDispos);
  }

  getFilteredDisponibilities(period: 'week' | 'month' | 'all'): IDispoResponse[] {
    const all = this.#disponibilities();
    if (period === 'all') return all;

    const now = new Date();
    return all.filter(dispo => {
      const dispoDate = new Date(dispo.dispo.date_dispo);

      if (period === 'week') {
        const oneWeek = new Date(now);
        oneWeek.setDate(now.getDate() + 7);
        return dispoDate >= now && dispoDate <= oneWeek;
      }

      if (period === 'month') {
        const oneMonth = new Date(now);
        oneMonth.setMonth(now.getMonth() + 1);
        return dispoDate >= now && dispoDate <= oneMonth;
      }

      return true;
    }).sort((a, b) => new Date(a.dispo.date_dispo).getTime() - new Date(b.dispo.date_dispo).getTime());
  }

  isDisponibilityFree(dispo: IDispoResponse): boolean {
    return !dispo.hours || dispo.hours.length === 0;
  }

  getOccupancyPercentage(dispo: IDispoResponse): number {
    if (!dispo.hours || dispo.hours.length === 0) return 0;

    const totalMinutes = this.getMinutesBetween(dispo.dispo.h_debut, dispo.dispo.h_fin);
    const reservedMinutes = dispo.hours.reduce((total, hour) => {
      return total + this.getMinutesBetween(hour.h_debut, hour.h_fin);
    }, 0);

    return Math.round((reservedMinutes / totalMinutes) * 100);
  }

  getMinutesBetween(startTime: string, endTime: string): number {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;

    return endTotal - startTotal;
  }
}

